import { selectorWithoutCache as selector } from 'util/RecoilWithoutCache'
import {
    MixContractFixedPositionInfo,
    MixContractInfo,
    MixContractPositionPushInfo,
    MixContractTokenInfo
} from '../entity'
import { MixContractHoldSide, MixContractMarginMode } from 'constants/MixContractGlobal'
import { createDefaultTokenTicker, MixContractTokenState } from './Token'
import {
    ContractPositionLevelList,
    createDefaultMixContractInfo,
    CurrentBusinessLineContractListState,
    getLevel
} from './Contract'
import { Decimal } from 'decimal.js'
import { calFixedReducePrice, calProfits, calValue, Zero } from 'helper/formula/MixContractFormula'
import {
    createEmptyAssetMode,
    getTotalUsdtValue,
    MixContractUserAccountModesState
} from './Account'
import StatisticResult from 'util/StatisticResult'
import { UserPreferenceSettingState } from './User'
import { PositionModeEnum } from 'constants/vars'
import { MinifiedTickerListAtom, getTicker } from '../v2/Ticker'

/**
 * 获取合约配置的工具函数
 * @param contractList
 * @param symbolId
 */
export function getContractConfig(contractList: MixContractInfo[], symbolId: string) {
    return contractList.find(c => c.symbolId === symbolId) || createDefaultMixContractInfo()
}

/**
 * 获取保证金ticker的工具函数
 * @param tokenTickers
 * @param tokenId
 * @param quoteTokenId
 */
function getTokenTicker(
    tokenTickers: MixContractTokenInfo[],
    tokenId: string,
    quoteTokenId: string
) {
    return (
        tokenTickers.find(t => t.baseTokenId === tokenId && t.quoteTokenId === quoteTokenId) ||
        createDefaultTokenTicker()
    )
}

/**
 * 仓位基本信息-》基本fixed信息
 */
export const PositionTransfer = selector({
    key: 'position-fixer',
    get: ({ get }) => {
        const modes = get(MixContractUserAccountModesState)
        const contractList = get(CurrentBusinessLineContractListState)
        // const tickers = get(MixContractAllTickersState)
        const tickers = get(MinifiedTickerListAtom)
        const tokenTickers = get(MixContractTokenState)
        const positionModeSet = get(UserPreferenceSettingState).positionModeSet
        return (position: MixContractPositionPushInfo): MixContractFixedPositionInfo => {
            const tokenId = position.tokenId
            const symbolId = position.symbolId
            const contractConfig = getContractConfig(contractList, symbolId)
            const contractLevelList = get(ContractPositionLevelList(contractConfig.symbolId))

            // FIXED_MARGIN = 1, // 逐仓模式
            // CROSS_MARGIN = 2 // 全仓模式
            const isFixedMode = position.marginMode === MixContractMarginMode.FIXED_MARGIN
            // 币对的合理标记价格
            // ui图 深度
            // markPrice 中间区域 靠右 的价格
            // bestAsk 最优卖1价   bestBid 最优卖1价 price 市场价格
            const { markPrice, price, bestAsk, bestBid } = getTicker(tickers, contractConfig)
            // 保证金币种的汇价  tokenMarkPrice = bp = usdt的单价
            const tokenMarkPrice = getTokenTicker(
                tokenTickers,
                tokenId,
                contractConfig.pricedSymbol
            ).indexPrice
            const feeRate = contractConfig.feeRate

            const dHold = new Decimal(position.available).add(position.locked)

            // 仓位价值 （Token），不带方向      markPrice => 标记价格
            let dValue = calValue(dHold ?? 0, markPrice ?? 0, tokenMarkPrice)
            // 开仓价仓位价值
            let dOpenValue = calValue(dHold ?? 0, position.averageOpenPrice ?? 0, tokenMarkPrice)

            // 实时仓位价值
            let dRealValue = calValue(dHold ?? 0, markPrice ?? 0, tokenMarkPrice)
            // 基本价值（按右币计量的价值，对于币本位的应该是USD单位）
            let dPricedValue = calValue(dHold ?? 0, markPrice ?? 0, 1)

            // 基本价值（按右币计量的价值，对于币本位的应该是USD单位） 新增一个用来取mm的
            let averageOpenPrice = position.averageOpenPrice
            const minPrice = Decimal.min(new Decimal(averageOpenPrice), new Decimal(markPrice))
            let dPricedValueForMM = calValue(dHold ?? 0, minPrice.toNumber() ?? 0, 1)

            let mode =
                modes.find(m => m.tokenId === tokenId && m.symbolId === symbolId) ||
                createEmptyAssetMode()
            let realLeverage: number
            //逐仓需要处理的是否自动追加保证金
            let newAutoAppendMargin = 0
            // 计算未实现盈亏（Token）
            let dUpnl = calProfits(
                position.averageOpenPrice,
                markPrice,
                dHold,
                tokenMarkPrice,
                position.holdSide
            )

            let dUpnlText = dUpnl.toFixed(
                contractConfig.placeConfig.propertyHighPlace,
                Decimal.ROUND_FLOOR
            )

            // 计算用于展示的，按照市场价格去计算的未实现盈亏（Token）
            let dUpnlUi = calProfits(
                position.averageOpenPrice,
                price,
                dHold,
                tokenMarkPrice,
                position.holdSide
            )

            let dUpnlTextUi = dUpnlUi.toFixed(
                contractConfig.placeConfig.propertyHighPlace,
                Decimal.ROUND_FLOOR
            )

            // 逐仓仓位，将保证金字段decimal化即可
            let dMargin: Decimal
            // 逐仓的保证金率、风险率、减仓价
            let dMarginRate: Decimal
            let dRiskRate = Zero
            let dReducePrice = Zero
            // 仓位，新增保证金用来计算可开 单向
            let dMargin4CalcCanOpen = Zero

            // 仓位所在档位信息
            let mm = '0'
            let level = 1
            let isSafe = false
            let dReducePriceValue = ''
            // 逐仓模式
            if (isFixedMode) {
                // 全仓模式，保证金=仓位价值/杠杆
                // 注意要从

                // 逐仓保证金估算
                if (positionModeSet === PositionModeEnum.ONE_WAY) {
                    dMargin4CalcCanOpen = dRealValue.div(mode.fixedLongLeverage)
                }
                dMargin = new Decimal(position.margin)
                // 逐仓模式直接记录即可
                let levelInfo = getLevel(contractLevelList, dHold.times(markPrice).toNumber())
                const minPrice = Decimal.min(markPrice, position.averageOpenPrice)
                let levelInfoForMM = getLevel(contractLevelList, dHold.times(minPrice).toNumber())
                mm = levelInfoForMM.keepMarginRate
                level = levelInfo.level

                // 风险率 = 维持保证金 /  现有保证金 * 100   算%
                dRiskRate = dValue // 仓位价值 =  数量 * 标记价格 (这个价格是深度中间靠右边的价格，考虑到币本位)
                    .times(new Decimal(mm).add(feeRate)) // 公式算到这里 为 逐仓维持保证金额 (不爆仓，最少要交这么多钱)
                    .div(dMargin.add(dUpnl)) // 上面除以 （ 保证金 + 未实现盈亏 ）
                    .times(100)

                // 多
                if (position.holdSide === MixContractHoldSide.LONG_POSITIONS) {
                    realLeverage = mode.fixedLongLeverage
                }
                // 空
                else {
                    realLeverage = mode.fixedShortLeverage
                }

                // 计算 逐仓的减仓价
                dReducePrice = calFixedReducePrice(
                    dHold, // 数量
                    position.averageOpenPrice, // 开仓价
                    dMargin, // 保证金
                    tokenMarkPrice, // bp 保证金币种价格
                    position.holdSide, // 方向
                    mm, // 维持保证金
                    feeRate // 手续费率
                )

                // 计算是否安全
                // 多
                if (position.holdSide === MixContractHoldSide.LONG_POSITIONS) {
                    isSafe = dReducePrice.lessThanOrEqualTo(
                        contractConfig.placeConfig.longLimitBurstPrice
                    )
                }
                // 空
                else {
                    isSafe =
                        dReducePrice.greaterThanOrEqualTo(
                            contractConfig.placeConfig.shortLimitBurstPrice
                        ) || dReducePrice.isNegative()
                }
                if (!isSafe) {
                    dReducePriceValue = dReducePrice.toFixed(
                        contractConfig.placeConfig.pricePlace,
                        position.holdSide === MixContractHoldSide.LONG_POSITIONS
                            ? Decimal.ROUND_UP
                            : Decimal.ROUND_DOWN
                    )
                } else {
                    // 永不爆仓
                }
                //逐仓模式才有的自动追加保证金
                if (positionModeSet === PositionModeEnum.ONE_WAY) {
                    newAutoAppendMargin = mode.singleHoldAutoMargin
                } else {
                    newAutoAppendMargin =
                        position.holdSide === MixContractHoldSide.LONG_POSITIONS
                            ? mode.fixedLongAutoMargin
                            : mode.fixedShortAutoMargin
                }
            } else {
                // 全仓保证金估算
                if (positionModeSet === PositionModeEnum.ONE_WAY) {
                    dMargin4CalcCanOpen = dRealValue.div(mode.crossMarginLeverage)
                }
                dMargin = dOpenValue.div(mode.crossMarginLeverage)
                realLeverage = mode.crossMarginLeverage
            }
            dMarginRate = dMargin.add(dUpnl).div(dRealValue).sub(feeRate).times(100)

            /**
             * https://bglimited.larksuite.com/docx/HrtYdU9LuoVyq7xMiMhubPxPsNb
             *
             * 初始保证金 =
             * 开仓均价*持仓数量/杠杆/保证金币指数价格 =
             * 仓位价值/杠杆倍数/保证金指数价格 =
             * dValue / realLeverage
             * 为什么不除以保证金指数价格：因为这里的dOpenValue已经是除过的了:这里给的单位如果是币本位，就是币。
             *
             * 回报率 = 未实现盈亏/初始保证金
             */
            const initialMargin = dOpenValue.div(realLeverage)
            // 收益率
            let dRoe = initialMargin.isZero() ? Zero : dUpnl.div(initialMargin).times(100)
            // 按照市场价格计算的收益率，用于展示
            let dRoeUi = initialMargin.isZero() ? Zero : dUpnlUi.div(initialMargin).times(100)
            // UI层开仓均价
            const averageOpenPriceUi = new Decimal(position.averageOpenPrice).toFixed(
                contractConfig?.placeConfig?.delegatePricePlace,
                position.holdSide === MixContractHoldSide.LONG_POSITIONS
                    ? Decimal.ROUND_UP
                    : Decimal.ROUND_DOWN
            )
            return {
                ...position,
                autoAppendMargin: newAutoAppendMargin || 0,
                dHold,
                leverage: realLeverage,
                dValue,
                dOpenValue,
                averageOpenPriceUi,
                dRealValue,
                dPricedValue,
                dPricedValueForMM,
                dUpnl,
                dUpnlText,
                dUpnlUi,
                dUpnlTextUi,
                dMargin,
                dMargin4CalcCanOpen,
                dRoe,
                dRoeUi,
                dReducePrice,
                dReducePriceValue,
                isSafe,
                dMarginRate,
                dRiskRate,
                mm,
                level,
                bestClosePrice:
                    position.holdSide === MixContractHoldSide.LONG_POSITIONS ? bestAsk : bestBid,
                baseSymbol: contractConfig.baseSymbol,
                bestClosePriceReverse:
                    position.holdSide === MixContractHoldSide.LONG_POSITIONS ? bestBid : bestAsk,
                dInitialMargin: initialMargin
            }
        }
    }
})

/**
 * PositionCollector  PositionCollectorV1 公用
 */

/**
 * 仓位信息聚合器
 */
export const PositionCollector = selector({
    key: 'position-collector',
    get: ({ get }) => {
        const contractList = get(CurrentBusinessLineContractListState)
        const tokenSymbolHoldUsdtValueForMM = get(getTotalUsdtValue).tokenSymbolHoldUsdtValueForMM

        return (positionList: MixContractFixedPositionInfo[]) => {
            // 某币种所开所有仓位（全+逐）未实现盈亏之合
            let tokenTotalUpnl = new StatisticResult()
            // 某币种所开所有仓位（全+逐）已实现盈亏之和
            let tokenTotalPnl = new StatisticResult()
            // 某币种所开所有逐仓仓位的保证金之和
            let tokenTotalFixedPositionMargin = new StatisticResult()
            // 某币种逐仓仓位的未实现盈利之和（这部分算权益，但是不能用于开仓）。注意逐仓的亏损仍然是要提前扣除
            let tokenTotalFixedPositionUnrealizedProfit = new StatisticResult()
            // 某币种全仓仓位的未实现盈亏（保证金计算）之和
            let tokenTotalCrossedPositionUnrealizedProfit = new StatisticResult()

            // 某币种下某合约的净头寸价值
            let tokenSymbolNetValueMap = new Map<string, StatisticResult>()

            // 某币种下某合约的净头寸（数量）
            let tokenSymbolNetHold = new StatisticResult()
            // 某币种下某合约的总持仓（数量）
            let tokenSymbolTotalHold = new StatisticResult()
            // 某币种某合约的大头持仓（价值）
            let tokenSymbolBiggerHoldValue = new Map<string, StatisticResult>()
            // 某币种某合约的大头持仓（数量）
            let tokenSymbolBiggerHold = new StatisticResult()
            // 某币种某合约的大头保证金
            let tokenSymbolBiggerMargin = new Map<string, StatisticResult>()
            // 单向新增 逐仓爆仓公式计算用
            // 某币对所开多头仓位未实现盈亏
            let tokenLongTotalUpnl = new StatisticResult()
            // 某币对所开空头仓位未实现盈亏
            let tokenShortTotalUpnl = new StatisticResult()

            for (let position of positionList) {
                const {
                    tokenId,
                    symbolId,
                    dUpnl,
                    dMargin,
                    dValue,
                    marginMode,
                    holdSide,
                    achievedProfits,
                    available,
                    locked
                } = position
                const unionKey = `${tokenId}_${symbolId}`
                const dHold = new Decimal(available).add(locked)

                // 总未实现盈亏
                tokenTotalUpnl.add(tokenId, dUpnl)
                // 总已实现盈亏
                tokenTotalPnl.add(tokenId, achievedProfits)
                if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
                    // 不分全仓逐仓
                    tokenLongTotalUpnl.add(unionKey, dUpnl)
                } else {
                    // 不分全仓逐仓
                    tokenShortTotalUpnl.add(unionKey, dUpnl)
                }
                // 区分逐仓全仓的统计
                if (marginMode === MixContractMarginMode.FIXED_MARGIN) {
                    tokenTotalFixedPositionMargin.add(tokenId, dMargin)
                    if (dUpnl.greaterThan(Zero)) {
                        tokenTotalFixedPositionUnrealizedProfit.add(tokenId, dUpnl)
                    }
                } else {
                    // 统计总持仓数量，用于获取仓位档位梯度
                    tokenSymbolTotalHold.add(unionKey, dHold)

                    // 统计净头寸数量
                    tokenSymbolNetHold.add(
                        unionKey,
                        dHold.times(holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1)
                    )

                    // 记录大头仓位的持仓量
                    let holdSymbols =
                        tokenSymbolBiggerHoldValue.get(tokenId) || new StatisticResult()

                    let biggerHold = holdSymbols.get(symbolId)
                    if (biggerHold.lessThan(dValue)) {
                        holdSymbols.set(symbolId, dValue)
                    }

                    tokenSymbolBiggerHoldValue.set(tokenId, holdSymbols)

                    // 统计大头仓位的保证金
                    let marginSymbols =
                        tokenSymbolBiggerMargin.get(tokenId) || new StatisticResult()

                    let biggerMargin = marginSymbols.get(symbolId)
                    if (biggerMargin.lessThan(dMargin)) {
                        marginSymbols.set(symbolId, dMargin)
                    }

                    tokenSymbolBiggerMargin.set(tokenId, marginSymbols)

                    let dCurrentHold = tokenSymbolBiggerHold.get(unionKey)
                    if (dCurrentHold.lessThan(dHold)) {
                        tokenSymbolBiggerHold.set(unionKey, dHold)
                    }

                    // 统计全仓仓位未实现盈亏
                    tokenTotalCrossedPositionUnrealizedProfit.add(tokenId, dUpnl)

                    // 统计净头寸价值
                    let netValueSymbols =
                        tokenSymbolNetValueMap.get(tokenId) || new StatisticResult()

                    let netValue = dValue.times(
                        holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1
                    )
                    netValueSymbols.add(symbolId, netValue)

                    tokenSymbolNetValueMap.set(tokenId, netValueSymbols)
                }
            }

            // 统计某保证金下所有交易对大头仓位的维持保证金之和
            const tokenCrossedMaintain = new StatisticResult()

            tokenSymbolBiggerHoldValue.forEach((symbols, tokenId) => {
                symbols.forEach((dValue, symbolId) => {
                    // 用合约总持仓获取维持保证金率
                    const totalUsdtValueForMM = tokenSymbolHoldUsdtValueForMM.get(
                        `${tokenId}_${symbolId}`
                    )
                    const contractConfig = getContractConfig(contractList, symbolId)
                    const contractLevelList = get(
                        ContractPositionLevelList(contractConfig.symbolId)
                    )
                    const feeRate = contractConfig.feeRate

                    // 注意换成usdt价值取
                    const { keepMarginRate } = getLevel(
                        contractLevelList,
                        totalUsdtValueForMM.toNumber()
                    )

                    // 但是某合约的维持保证金额只需要用大头仓位价值*维持保证金率（保证金币种记）
                    // 追加taker手续费率，增大所需维持保证金数额
                    const maintain = dValue.times(new Decimal(keepMarginRate).add(feeRate))
                    tokenCrossedMaintain.add(tokenId, maintain)
                })
            })

            // 统计某保证金下所有交易对大头仓位的开仓保证金之和
            const tokenCrossedMargin = new StatisticResult()

            // 根据大头算总保证金量
            tokenSymbolBiggerMargin.forEach((symbols, tokenId) => {
                symbols.forEach((dMargin, _) => {
                    tokenCrossedMargin.add(tokenId, dMargin)
                })
            })

            // 统计某保证金下所有合约的净头寸价值
            const tokenTotalNetValue = new StatisticResult()
            // 统计某保证金下的某个合约的净头寸价值
            const tokenSymbolNetValue = new StatisticResult()

            tokenSymbolNetValueMap.forEach((symbols, tokenId) => {
                let sum = Zero
                symbols.forEach((value, symbolId) => {
                    sum = sum.add(value.abs())
                    tokenSymbolNetValue.set(`${tokenId}_${symbolId}`, value.abs())
                })
                tokenTotalNetValue.set(tokenId, sum)
            })

            return {
                tokenTotalUpnl,
                tokenTotalPnl,
                tokenTotalFixedPositionMargin,
                tokenTotalFixedPositionUnrealizedProfit,
                tokenCrossedMaintain,
                tokenTotalCrossedPositionUnrealizedProfit,
                tokenTotalNetValue,
                tokenSymbolNetValue,
                tokenSymbolNetHold,
                tokenSymbolTotalHold,
                tokenSymbolBiggerHoldValue,
                tokenSymbolBiggerHold,
                tokenCrossedMargin,
                tokenLongTotalUpnl,
                tokenShortTotalUpnl
            }
        }
    }
})

// 重写 PositionCollector  可以拿到 MixContractBasePositionListSelector 132行用
// 新增 弹窗爆仓价格
export const PositionCollectorV1 = selector({
    key: 'position-collector-v1',
    get: ({ get }) => {
        const contractList = get(CurrentBusinessLineContractListState)
        const _tokenSymbolHoldUsdtValueForMM = get(getTotalUsdtValue).tokenSymbolHoldUsdtValueForMM
        // const _tokenSymbolSideUsdtValue = get(getTotalUsdtValue).tokenSymbolSideUsdtValue
        // tokenSymbolHoldUsdtValueForMM  这块有个闭包，返回出去的函数，必须入参还能拿到最新的 这个参数在该函数里面只用来取保证金比率 注意 不是原来的（仓位大头价值+ 委托价值了）
        // tokenSymbolHoldUsdtValue  主要计算各交易对 持仓价值(包含即将开仓)
        return (positionList: MixContractFixedPositionInfo[], tokenSymbolHoldUsdtValueForMM) => {
            tokenSymbolHoldUsdtValueForMM =
                tokenSymbolHoldUsdtValueForMM || _tokenSymbolHoldUsdtValueForMM
            // 某币种所开所有仓位（全+逐）未实现盈亏之合
            let tokenTotalUpnl = new StatisticResult()
            // 某币种所开所有仓位（全+逐）已实现盈亏之和
            let tokenTotalPnl = new StatisticResult()
            // 某币种所开所有逐仓仓位的保证金之和
            let tokenTotalFixedPositionMargin = new StatisticResult()
            // 某币种逐仓仓位的未实现盈利之和（这部分算权益，但是不能用于开仓）。注意逐仓的亏损仍然是要提前扣除
            let tokenTotalFixedPositionUnrealizedProfit = new StatisticResult()
            // 某币种全仓仓位的未实现盈亏（保证金计算）之和
            let tokenTotalCrossedPositionUnrealizedProfit = new StatisticResult()

            // 某币种下某合约的净头寸价值
            let tokenSymbolNetValueMap = new Map<string, StatisticResult>()

            // 某币种下某合约的净头寸（数量）
            let tokenSymbolNetHold = new StatisticResult()
            // 某币种下某合约的总持仓（数量）
            let tokenSymbolTotalHold = new StatisticResult()
            // 某币种某合约的大头持仓（价值）
            let tokenSymbolBiggerHoldValue = new Map<string, StatisticResult>()
            // 某币种某合约的大头持仓（数量）
            let tokenSymbolBiggerHold = new StatisticResult()
            // 某币种某合约的大头保证金
            let tokenSymbolBiggerMargin = new Map<string, StatisticResult>()

            // 某保证金下所有币对的净头寸（数量）
            let tokenTokenNetHold = new StatisticResult()

            for (let position of positionList) {
                const {
                    tokenId,
                    symbolId,
                    dUpnl,
                    dMargin,
                    dValue,
                    marginMode,
                    holdSide,
                    achievedProfits,
                    available,
                    locked
                } = position
                const unionKey = `${tokenId}_${symbolId}`
                const dHold = new Decimal(available).add(locked)

                // 总未实现盈亏
                tokenTotalUpnl.add(tokenId, dUpnl)
                // 总已实现盈亏
                tokenTotalPnl.add(tokenId, achievedProfits)

                // 区分逐仓全仓的统计
                if (marginMode === MixContractMarginMode.FIXED_MARGIN) {
                    tokenTotalFixedPositionMargin.add(tokenId, dMargin)
                    if (dUpnl.greaterThan(Zero)) {
                        tokenTotalFixedPositionUnrealizedProfit.add(tokenId, dUpnl)
                    }
                } else {
                    // 统计总持仓数量，用于获取仓位档位梯度
                    tokenSymbolTotalHold.add(unionKey, dHold)

                    // 统计净头寸数量
                    tokenSymbolNetHold.add(
                        unionKey,
                        dHold.times(holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1)
                    )
                    tokenTokenNetHold.add(
                        tokenId,
                        dHold.times(holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1)
                    )

                    // 记录大头仓位的持仓量
                    let holdSymbols =
                        tokenSymbolBiggerHoldValue.get(tokenId) || new StatisticResult()

                    let biggerHold = holdSymbols.get(symbolId)
                    if (biggerHold.lessThan(dValue)) {
                        holdSymbols.set(symbolId, dValue)
                    }

                    tokenSymbolBiggerHoldValue.set(tokenId, holdSymbols)

                    // 统计大头仓位的保证金
                    let marginSymbols =
                        tokenSymbolBiggerMargin.get(tokenId) || new StatisticResult()

                    let biggerMargin = marginSymbols.get(symbolId)
                    if (biggerMargin.lessThan(dMargin)) {
                        marginSymbols.set(symbolId, dMargin)
                    }

                    tokenSymbolBiggerMargin.set(tokenId, marginSymbols)

                    let dCurrentHold = tokenSymbolBiggerHold.get(unionKey)
                    if (dCurrentHold.lessThan(dHold)) {
                        tokenSymbolBiggerHold.set(unionKey, dHold)
                    }

                    // 统计全仓仓位未实现盈亏
                    tokenTotalCrossedPositionUnrealizedProfit.add(tokenId, dUpnl)

                    // 统计净头寸价值
                    let netValueSymbols =
                        tokenSymbolNetValueMap.get(tokenId) || new StatisticResult()

                    let netValue = dValue.times(
                        holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1
                    )
                    netValueSymbols.add(symbolId, netValue)

                    tokenSymbolNetValueMap.set(tokenId, netValueSymbols)
                }
            }

            // 统计某保证金下所有交易对大头仓位的维持保证金之和
            const tokenCrossedMaintain = new StatisticResult()
            /* 计算某币对的维持保证金 */
            const tokenSymbolCrossedMaintain = new Map<string, Decimal>()

            tokenSymbolBiggerHoldValue.forEach((symbols, tokenId) => {
                symbols.forEach((dValue, symbolId) => {
                    // 用合约总持仓获取维持保证金率
                    const totalUsdtValueForMM = tokenSymbolHoldUsdtValueForMM.get(
                        `${tokenId}_${symbolId}`
                    )
                    const contractConfig = getContractConfig(contractList, symbolId)
                    const contractLevelList = get(
                        ContractPositionLevelList(contractConfig.symbolId)
                    )
                    const feeRate = contractConfig.feeRate

                    // 注意换成usdt价值取     获取手续费 下面再减去
                    // mm   ==> keepMarginRate  保证金率
                    const { keepMarginRate } = getLevel(
                        contractLevelList,
                        totalUsdtValueForMM.toNumber()
                    )

                    // 但是某合约的维持保证金额只需要用大头仓位价值*维持保证金率（保证金币种记）
                    // 追加taker手续费率，增大所需维持保证金数额
                    const maintain = dValue.times(new Decimal(keepMarginRate).add(feeRate))
                    tokenCrossedMaintain.add(tokenId, maintain)
                    tokenSymbolCrossedMaintain.set(`${tokenId}_${symbolId}`, maintain)
                })
            })

            // 统计某保证金下所有交易对大头仓位的开仓保证金之和
            const tokenCrossedMargin = new StatisticResult()

            // 根据大头算总保证金量
            tokenSymbolBiggerMargin.forEach((symbols, tokenId) => {
                symbols.forEach((dMargin, _) => {
                    tokenCrossedMargin.add(tokenId, dMargin)
                })
            })

            // 统计某保证金下所有合约的净头寸价值
            const tokenTotalNetValue = new StatisticResult()
            // 统计某保证金下的某个合约的净头寸价值
            const tokenSymbolNetValue = new StatisticResult()

            tokenSymbolNetValueMap.forEach((symbols, tokenId) => {
                let sum = Zero
                symbols.forEach((value, symbolId) => {
                    sum = sum.add(value.abs())
                    tokenSymbolNetValue.set(`${tokenId}_${symbolId}`, value.abs())
                })
                tokenTotalNetValue.set(tokenId, sum)
            })

            return {
                tokenTotalUpnl,
                tokenTotalPnl,
                tokenTotalFixedPositionMargin,
                tokenTotalFixedPositionUnrealizedProfit,
                tokenCrossedMaintain,
                tokenSymbolCrossedMaintain,
                tokenTotalCrossedPositionUnrealizedProfit,
                tokenTotalNetValue,
                tokenSymbolNetValue,
                tokenSymbolNetHold,
                tokenSymbolTotalHold,
                tokenSymbolBiggerHoldValue,
                tokenSymbolBiggerHold,
                tokenCrossedMargin,
                tokenTokenNetHold
            }
        }
    }
})
